import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../Icon/Icon';
import { Skeleton } from '../Skeleton/Skeleton';
import { Text } from '../Text/Text';
import {
  Branch,
  Chevron,
  Count,
  Empty,
  Hint,
  Item,
  Label,
  Name,
  PageRow,
  Placeholder,
  Root,
  SearchBox,
  SearchIcon,
  SearchInput,
  Spacer,
  Toggle,
  Tree,
} from './PageTree.styles';

/** A page under a top page: its path, its name as the site shows it, and an optional hint after the name (already
 *  translated: "every page" on a route that serves many urls). */
export type PageTreeChild = { path: string; label: string; hint?: string };

/** A top page of the site, with the pages under it, both in the site's order. */
export type PageTreeNode = PageTreeChild & { children: PageTreeChild[] };

export type PageTreeProps = {
  nodes: PageTreeNode[];
  /** The page on screen. */
  openPath: string;
  onOpen: (path: string) => void;
  /** While the site's pages are still being decided: the tree says it is busy, with placeholder rows after the
   *  pages already known. */
  loading?: boolean;
  /** Marks at the end of a page's row (top or under a branch), for the eye and announced with its name: give each one
   *  `role="img"` and an `aria-label`. */
  trailing?: (path: string) => React.ReactNode;
};

/** Placeholder rows' widths while the pages are decided: names of a few lengths, so the column reads as a list. */
const PLACEHOLDER_WIDTHS = [96, 120, 84, 108] as const;

/** `value` in lower case without its accents ("Lotería" → "loteria"). */
const foldText = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

/** Whether `label` holds the search, in any case and with or without its accents (a search for "loteria" finds
 *  "Lotería", as the list filter does). Every name holds a blank search. */
const holds = (label: string, wanted: string): boolean => foldText(label).includes(wanted);

/** The tree a search leaves: a top page that holds it, with the pages under it that hold it too, and a top page that
 *  does not, kept for the pages under it that do. Every page for a blank search. */
const matchingNodes = (nodes: PageTreeNode[], query: string): PageTreeNode[] => {
  const wanted = foldText(query.trim());
  if (wanted === '') return nodes;
  return nodes
    .map((node) => ({ ...node, children: node.children.filter((child) => holds(child.label, wanted)) }))
    .filter((node) => holds(node.label, wanted) || node.children.length > 0);
};

/**
 * The site's pages, to pick the one a tab shows (Game Lists, SEO): a search field, then each top page (Homepage, Casino, Live
 * Casino, ...) as a row, with the pages under it (Live Casino: Blackjack, Roulette, ...) in a branch its toggle opens
 * and closes. A top page's name opens the page itself; a page's name under it opens that page.
 *
 * Only the branch of the open page starts open, so the tree stays short on a site with many pages. A branch the owner
 * opened or closed by hand keeps that; one they never touched follows the open page. A search shows the pages that
 * match, each under the page it sits under, with every branch that has a match open.
 */
export const PageTree = ({ nodes, openPath, onOpen, loading = false, trailing }: PageTreeProps): React.ReactElement => {
  const { t } = useTranslation();
  const baseId = React.useId();
  const [query, setQuery] = React.useState('');
  // The branches the owner opened (true) or closed (false) by hand; every other branch is open while it holds the
  // open page, and closed otherwise.
  const [handSet, setHandSet] = React.useState<ReadonlyMap<string, boolean>>(() => new Map());
  const searching = query.trim() !== '';
  const shown = matchingNodes(nodes, query);

  // The top page the open one sits under, read from every page rather than the ones a search shows, so a search that
  // hides the open page still shows where the owner is.
  const openParent = nodes.find((node) => node.children.some((child) => child.path === openPath))?.path;
  const holdsOpen = (node: PageTreeNode): boolean => node.path === openParent;
  const isOpen = (node: PageTreeNode): boolean =>
    searching ? node.children.length > 0 : handSet.get(node.path) ?? (node.path === openPath || holdsOpen(node));

  const setBranch = (path: string, open: boolean | null): void =>
    setHandSet((current) => {
      const next = new Map(current);
      if (open === null) next.delete(path);
      else next.set(path, open);
      return next;
    });

  const onToggle = (node: PageTreeNode): void => {
    // A search decides which branches are open, and the tree shows no toggle while one is typed.
    if (searching) return;
    setBranch(node.path, !isOpen(node));
  };
  // A top page's name opens the page and, with it, its branch: it is the open page's branch again, whatever the owner
  // last did with its toggle.
  const onOpenTop = (node: PageTreeNode): void => {
    setBranch(node.path, null);
    onOpen(node.path);
  };

  return (
    <Root aria-label={t('Website pages')} aria-busy={loading ? 'true' : undefined}>
      <SearchBox>
        <SearchIcon aria-hidden="true">
          <Icon name="search" size={16} />
        </SearchIcon>
        <SearchInput
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('Find a page')}
          aria-label={t('Find a page')}
        />
      </SearchBox>
      <Tree>
        {shown.map((node, index) => {
          const open = isOpen(node);
          const branchId = `${baseId}-branch-${index}`;
          const hasBranch = node.children.length > 0;
          const isCurrent = node.path === openPath;
          return (
            <Item key={node.path}>
              <PageRow>
                {hasBranch && !searching ? (
                  <Toggle
                    type="button"
                    aria-expanded={open}
                    aria-controls={open ? branchId : undefined}
                    aria-label={t('Pages under {{name}}', { name: node.label })}
                    onClick={() => onToggle(node)}
                  >
                    <Chevron $open={open} aria-hidden="true">
                      <Icon name="chevron-down" size={16} />
                    </Chevron>
                  </Toggle>
                ) : (
                  <Spacer aria-hidden="true" />
                )}
                <Name
                  type="button"
                  // The open page, or the top page the open one sits under (in the brand orange too, so a closed
                  // branch still shows where the owner is).
                  aria-current={isCurrent ? 'page' : holdsOpen(node) ? 'true' : undefined}
                  // The whole name, for when the column cuts it with an ellipsis.
                  title={node.label}
                  // The page's path, for tests and the caller's own lookups.
                  data-path={node.path}
                  onClick={() => onOpenTop(node)}
                >
                  <Label text={node.label} tag="span" raw />
                  {node.hint ? <Hint text={node.hint} tag="span" raw /> : null}
                  {/* How many pages a closed branch holds: for the eye only, the toggle says whether it is open. */}
                  {hasBranch && !open && !searching ? (
                    <Count aria-hidden="true">
                      <Text text={String(node.children.length)} tag="span" raw />
                    </Count>
                  ) : null}
                  {trailing?.(node.path)}
                </Name>
              </PageRow>
              {hasBranch && open ? (
                // Named after its top page, so it is announced as that page's list of pages.
                <Branch id={branchId} aria-label={node.label}>
                  {node.children.map((child) => (
                    <Item key={child.path}>
                      <Name
                        type="button"
                        $child
                        aria-current={child.path === openPath ? 'page' : undefined}
                        title={child.label}
                        data-path={child.path}
                        onClick={() => onOpen(child.path)}
                      >
                        <Label text={child.label} tag="span" raw />
                        {child.hint ? <Hint text={child.hint} tag="span" raw /> : null}
                        {trailing?.(child.path)}
                      </Name>
                    </Item>
                  ))}
                </Branch>
              ) : null}
            </Item>
          );
        })}
        {loading && !searching
          ? PLACEHOLDER_WIDTHS.map((width) => (
              <Placeholder key={width} aria-hidden="true" data-testid="page-tree-placeholder">
                <Skeleton width={width} height={14} radius={7} />
              </Placeholder>
            ))
          : null}
      </Tree>
      {searching && shown.length === 0 ? <Empty text="No pages match." tag="paragraph" /> : null}
    </Root>
  );
};

export default PageTree;
